import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { selectAssets } from 'src/store/selectors/assets';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { setSearch, clearSearch } from 'src/store/actions/search';
import LoadState from 'src/components/LoadState/LoadState';
import { selectPRLoader } from 'src/store/selectors/PropertyLoader';
import AreaAssetsTable from '../../components/AssetsTable/AreaAssetsTable';
import useUnitAreas from 'src/SharedCustomHooks/UseUnitAreas/useUnitAreas';
import {
  saveAsset,
  updateAsset,
  deleteAsset as legacyDeleteAssetAction,
  fetchAssetsBySuiteId
} from 'src/store/actions/assets';
import { fetchSuiteCounts } from 'src/store/actions/suites';

const SuiteAssets = (props: any) => {
  const { suite, history, property } = props;
  const dispatch = useDispatch();

  const assets = useSelector(selectAssets);
  const user = useSelector(selectAuthenticatedUser) as any;
  const loader = useSelector(selectPRLoader);

  /* FIXME: DISTRIBUTED STATE DEBT
   * We are mixing local hook state (useUnitAreas) with global Redux state (BOS legacy).
   * This UI component is mid migration. The new 'deleteUnitArea' logic lives in the hook, but asset-level management
   * remains in Redux to satisfy legacy side-effects (toasts, validation, global refreshes). */
  const {
    loading: deleteRoomLoading,
    deleteUnitArea,
    clearError,
  } = useUnitAreas({
    suiteId: suite.id,
    userId: user?.id,
    roleName: user?.roles?.[0]?.role?.name,
  });

  useEffect(() => {
    // TODO: Transition to hook-based data fetching once selectAssets is decoupled from other pages.
    dispatch(fetchAssetsBySuiteId(suite.id) as any);
    dispatch(clearSearch() as any);
  }, [suite.id, dispatch]);

  const onSaveAsset = async (asset: any) => {
    let params: any = {};

    if (asset.id) {
      params = {
        id: asset.id,
        Name: asset.item.name,
        AreaId: asset.areaId,
        unitId: suite.id,
        ItemId: asset.itemId ? asset.itemId : asset.item.id,
        RoomType: asset.roomType,
        UserId: user.id,
        DateOfPurchase: asset.dateOfPurchase
          ? new Date(moment(asset.dateOfPurchase).format('l LT'))
          : null,
        VendorId: asset?.vendorId || null,
        VendorName: asset?.vendorId ? asset.vendorName : '',
      };

      /* TODO: REDUX SPAGHETTI BRIDGE
       * updateAsset relies on the global 'validation' slice to trigger modal closure.
       * Decouple this by moving to a Promise-based callback pattern in a future sprint. */
      dispatch(updateAsset(params) as any);
    } else {
      params = {
        UnitId: suite.id,
        Name: asset.item.name,
        AreaId: asset.areaId,
        ItemId: asset.itemId,
        RoomType: asset.roomType,
        UserId: user.id,
        DateOfPurchase: asset.dateOfPurchase
          ? new Date(moment(asset.dateOfPurchase).format('l LT'))
          : new Date(),
        VendorId: asset?.vendorId || null,
        VendorName: asset?.vendorId ? asset.vendorName : '',
        allRooms: asset.allRooms === true,
      };

      if (!asset.vendorId) {
        delete params.VendorId;
        delete params.VendorName;
      }

      /* FIXME: ATOMICITY RISK
       * saveAsset thunk triggers a waterfall of side-effects (toasts, validation success).
       * We manually follow it with fetchSuiteCounts because the Redux state doesn't
       * automatically sync property/suite counts after an asset addition. */
      await dispatch(saveAsset(params) as any);
      dispatch(fetchSuiteCounts(suite.id, user.roles[0].role.name) as any);
    }
  };

  const onDeleteAsset = async (areaId: string, assetId: string) => {
    /*TODO: LEGACY BOS COUPLING
     * We use legacyDeleteAssetAction because it contains hard-coded toast logic and
     * specific Redux dispatches required by the global assets tree. */
    await dispatch(legacyDeleteAssetAction(areaId, assetId, null, null, null, user.id) as any);
    dispatch(fetchSuiteCounts(suite.id, user.roles[0].role.name) as any);
  };

  return (
    <AreaAssetsTable
      suite={suite}
      history={history}
      property={property}
      assets={assets}
      onSaveAsset={onSaveAsset}
      onDeleteAsset={onDeleteAsset}
      onDeleteRoom={deleteUnitArea}
      deleteRoomLoading={deleteRoomLoading}
      onClearDeleteRoomError={clearError}
      onSearch={(e: any) =>
        dispatch(setSearch({ assets: e.target.value.trim() }) as any)
      }
      introContent={true}
      loading={loader}
    />
  );
};

export default SuiteAssets;